import React, { Dispatch, useRef } from "react";
import { useIntl } from "react-intl";
import { DefaultButton, MessageBar, MessageBarType } from "@fluentui/react";
import { TextField } from "cayo.ui";
import styled from "styled-components";
import { messages } from "../messages";

interface IAppCertForm {
  dispatchWizardState: Dispatch<{ type: string; payload: any }>;
  wizardState: any;
}

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== "string") {
        reject(new Error("Invalid certificate file"));
        return;
      }

      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

export const AppCertForm = ({ wizardState, dispatchWizardState }: IAppCertForm) => {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);

      dispatchWizardState({
        type: "update",
        payload: {
          certificate: base64,
          certificateFileName: file.name,
          certificateError: "",
        },
      });
    } catch {
      dispatchWizardState({
        type: "update",
        payload: {
          certificate: "",
          certificateFileName: "",
          certificateError: intl.formatMessage(messages.certificateInvalidError),
        },
      });
    }

    // Reset so the same file can be re-selected if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <View>
      <TextField
        label={intl.formatMessage(messages.applicationIdLabel)}
        required
        value={wizardState.applicationId}
        setValue={(value) =>
          dispatchWizardState({ type: "update", payload: { applicationId: value } })
        }
      />

      <TextField
        label={intl.formatMessage(messages.tenantIdLabel)}
        required
        value={wizardState.tenantId}
        setValue={(value) => dispatchWizardState({ type: "update", payload: { tenantId: value } })}
      />

      <CertificateField>
        <CertLabel>
          {intl.formatMessage(messages.certificateLabel)} <Required>*</Required>
        </CertLabel>

        <CertRow>
          <DefaultButton
            text={intl.formatMessage(messages.uploadCertificateButton)}
            onClick={() => fileInputRef.current?.click()}
            styles={{ root: { marginTop: 4 } }}
          />
          {wizardState.certificateFileName && (
            <FileName>{wizardState.certificateFileName}</FileName>
          )}
        </CertRow>

        <CertHelperText>{intl.formatMessage(messages.certificateHelperText)}</CertHelperText>

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept=".pfx,.p12"
          onChange={handleFileChange}
        />

        {wizardState.certificateError && (
          <MessageBar
            messageBarType={MessageBarType.error}
            truncated={false}
            isMultiline
            styles={{ root: { marginTop: 4 } }}
          >
            {wizardState.certificateError}
          </MessageBar>
        )}
      </CertificateField>

      <TextField
        label={intl.formatMessage(messages.certificatePasswordLabel)}
        value={wizardState.certificatePassword}
        isConfidential
        placeHolder={intl.formatMessage(messages.certificatePasswordPlaceholder)}
        setValue={(value) =>
          dispatchWizardState({ type: "update", payload: { certificatePassword: value } })
        }
      />
    </View>
  );
};

const View = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    &::after {
      padding-right: 0 !important;
    }
  }
`;

const CertificateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const CertLabel = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const Required = styled.span`
  color: ${(props) => props.theme.cayoTheme.brandColors.error};
`;

const CertRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CertHelperText = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.cayoTheme.brandColors.secondaryText};
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileName = styled.span`
  font-size: 14px;
`;
